/**
 * **Where an abandoned session stopped** — every exercise, with the sets done and the ones never
 * started. `GET /workout-execution/{id}/display` has no completion gate, so the half that was done
 * renders (the same read the Pulpit's „Zobacz sesję" opens); the item names the execution
 * (`AbandonedWorkoutDetector`), and without one the block draws nothing.
 *
 * The message the canon drew under it goes to the chat, which is out of this app's v1: on the desk
 * that is the card's „Napisz DM" (owner, 25 September 2026), which leads where the conversation is.
 */

import { useRef } from 'react';

import { useOnScreen } from '~/shared/lib/useOnScreen';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import { useSessionReadout } from '../lib/useSessionReadout';
import type { TaskDensity } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { BlockRead } from './BlockRead';
import { ExerciseLines } from './ExerciseLines';
import styles from './taskBlocks.module.css';

export function AbandonedBlock({ item, density }: { readonly item: AttentionItem; readonly density: TaskDensity }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const { detail, detailFailed, chipsPending } = useSessionReadout(
    {
      kind: 'session',
      freestyle: false,
      workoutExecutionId: item.workoutExecutionId,
      name: item.triage.sessionName,
      setsDone: item.triage.sessionSetsDone,
      setsTotal: item.triage.sessionSetsTotal,
      durationSeconds: null,
    },
    useOnScreen(boxRef),
  );
  const gate = useSkeletonGate(chipsPending);

  return (
    <div className={`${String(styles.stack)} ${String(styles[density])}`} ref={boxRef} aria-busy={gate.waiting}>
      {item.workoutExecutionId === null ? null : (
        <BlockRead gate={gate} reserve={styles.reserveLines} failed={detailFailed}>
          {detail === null ? null : <ExerciseLines lines={detail.all} limit={density === 'desk' ? 8 : 4} />}
        </BlockRead>
      )}
    </div>
  );
}
